import { useEffect, useRef, useState } from 'react'
import * as v from '@badrap/valita'
import { throwUnsupportedValue } from 'src/utils'

type Storage = 'local' | 'session'

const clearCacheKey = 'storage_clear_cache'

export function clearStorageCache(category: string) {
  window.dispatchEvent(
    new CustomEvent(clearCacheKey, {
      detail: category,
    }),
  )
}

export function createStorageCategory(storage: Storage, category: string) {
  let items: { clear: () => void }[] = []
  let resetItems: { clear: () => void }[] = []

  window.addEventListener(clearCacheKey, function (e: CustomEvent<string>) {
    if (
      storage === 'session' &&
      (e.detail === 'ALL' || e.detail === category)
    ) {
      for (let item of items) {
        item.clear()
      }

      items = [...resetItems]
      resetItems = []
    }
  } as EventListener)

  return function <T>(key: string, schema: v.Type<T>) {
    const itemKey = `${category}_${key}`
    const eventKey = `storage_${storage}_${itemKey}`
    let cachedValue: T | null = null

    function get() {
      if (cachedValue !== null) {
        return cachedValue
      }

      let item = null
      switch (storage) {
        case 'local':
          item = localStorage.getItem(itemKey)
          break
        case 'session':
          item = sessionStorage.getItem(itemKey)
          break
        default:
          throwUnsupportedValue(storage)
      }

      if (!item) {
        console.log(`${itemKey} wasn't found in ${storage}Storage`)
        return null
      }

      const formattedValue = schema.try(JSON.parse(item))

      if (!formattedValue.ok) {
        console.warn(
          `an error occurred parsing: ${key} from ${storage}Storage:`,
          formattedValue.message,
        )
        return null
      }

      cachedValue = formattedValue.value
      return formattedValue.value
    }

    function set(value: T) {
      cachedValue = value

      const valueToStore = JSON.stringify(value)
      switch (storage) {
        case 'local':
          localStorage.setItem(itemKey, valueToStore)
          break
        case 'session':
          sessionStorage.setItem(itemKey, valueToStore)
          break
        default:
          throwUnsupportedValue(storage)
      }
    }

    function setStandalone(value: T) {
      set(value)
      window.dispatchEvent(new CustomEvent(eventKey, { detail: value }))
    }

    function clear() {
      cachedValue = null
      window.dispatchEvent(new CustomEvent(eventKey, { detail: null }))

      switch (storage) {
        case 'local':
          localStorage.removeItem(itemKey)
          break
        case 'session':
          sessionStorage.removeItem(itemKey)
          break
        default:
          throwUnsupportedValue(storage)
      }
    }

    function useStorageState(defaultValue: T) {
      const [value, setValue] = useState(() => get() || defaultValue)
      const cachedDefaultValue = useRef(defaultValue)

      useEffect(() => {
        cachedDefaultValue.current = defaultValue
      }, [defaultValue])

      useEffect(() => {
        function update(e: CustomEvent<T>) {
          if (e.detail === null) {
            setValue(cachedDefaultValue.current)
            set(cachedDefaultValue.current)
            resetItems.push({ clear })
          } else {
            setValue(e.detail)
          }
        }

        window.addEventListener(eventKey, update as EventListener)
        return () => {
          window.removeEventListener(eventKey, update as EventListener)
        }
      }, [])

      useEffect(() => {
        if (value !== cachedValue) {
          setStandalone(value)
        }
      }, [value])

      return [value, setValue] as const
    }

    items.push({ clear })

    return {
      get,
      clear,
      set: setStandalone,
      useState: useStorageState,
    }
  }
}
